// src/pages/OrderTrackingPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { API_CONFIG } from '../utils/apiConfig';
import { showToast } from "../utils/helpers";
import HeaderLanding from "../components/partials/HeaderLanding"

const OrderTrackingPage = () => {
  const { orderId: urlOrderId } = useParams();
  const navigate = useNavigate();

  const [orderId, setOrderId] = useState(urlOrderId || "");
  const [orderData, setOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showComingSoon, setShowComingSoon] = useState(false);

  // If order ID is provided in URL, fetch order data automatically
  useEffect(() => {
    if (urlOrderId) {
      setOrderId(urlOrderId);
      fetchOrderData(urlOrderId);
    }
  }, [urlOrderId]);

  const fetchOrderData = async (id) => {
    if (!id.trim()) {
      setError("Please enter an order ID");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await api.get(`${API_CONFIG.ORDERS.ORDER_TRACK}/${id}`);

      if (response.data.success) {
        setOrderData(response.data.order);
        // Update URL without reloading the page
        if (id !== urlOrderId) {
          navigate(`/track/${id}`, { replace: true });
        }
      } else {
        setError(response.data.message || "Order not found");
        setOrderData(null);
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      setError(error.response?.data?.message || "Failed to fetch order details");
      setOrderData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchOrderData(orderId);
  };

  const handleGenerateInvoice = () => {
    setShowComingSoon(true);
  };

  const handleSchedulePayment = () => {
    setShowComingSoon(true);
  };

  const formatCurrency = (amount, currency = 'NGN') => {
    if (!amount) return currency === 'NGN' ? '₦0.00' : '$0.00';

    try {
      const options = {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      };

      return new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-US', options).format(amount);
    } catch (error) {
      return currency === 'NGN' ? `₦${amount.toFixed(2)}` : `$${amount.toFixed(2)}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'delivered':
      case 'completed':
        return "bg-green-100 text-green-800";
      case 'processing':
      case 'pending':
      case 'payment_pending':
        return "bg-yellow-100 text-yellow-800";
      case 'shipped':
        return "bg-blue-100 text-blue-800";
      case 'cancelled':
      case 'failed':
        return "bg-red-100 text-red-800";
      case 'returned':
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 ";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
        return "✅";
      case 'delivered':
        return "📦";
      case 'processing':
        return "🔄";
      case 'pending':
      case 'payment_pending':
        return "⏳";
      case 'shipped':
        return "🚚";
      case 'cancelled':
        return "❌";
      case 'failed':
        return "⚠️";
      case 'returned':
        return "↩️";
      default:
        return "❓";
    }
  };

  const getStatusSteps = (order) => {
    if (order?.order_type === 'service') {
      return [
        { id: 1, name: 'Booking Received', completed: order.progress?.[0]?.completed || false },
        { id: 2, name: 'Deal Accepted', completed: order.progress?.[1]?.completed || false },
        { id: 3, name: 'Payment Scheduled', completed: order.progress?.[2]?.completed || false },
        { id: 4, name: 'Payment Completed', completed: order.progress?.[3]?.completed || false },
        { id: 5, name: 'Service Completed', completed: order.progress?.[4]?.completed || false },
      ];
    } else {
      return [
        { id: 1, name: 'Order Placed', completed: true },
        { id: 2, name: 'Payment Confirmed', completed: order?.payment_status === 'paid' },
        { id: 3, name: 'Processing', completed: ['processing', 'shipped', 'delivered', 'completed'].includes(order?.order_status) },
        { id: 4, name: 'Shipped', completed: ['shipped', 'delivered', 'completed'].includes(order?.order_status) },
        { id: 5, name: 'Delivered', completed: ['delivered', 'completed'].includes(order?.order_status) }
      ];
    }
  };

  const isServiceOrder = orderData?.order_type === 'service';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <HeaderLanding />

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🚀</span>
              </div>
              <h3 className="text-xl font-bold  mb-2">Coming Soon!</h3>
              <p className="text-gray-600 mb-6">
                This feature is currently under development and will be available soon.
              </p>
              <button
                onClick={() => setShowComingSoon(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold  mb-2 py-4">Track Your {isServiceOrder ? 'Service' : 'Order'}</h1>
        <p className="text-gray-600">
          {isServiceOrder 
            ? "Enter your booking ID to check the status of your service" 
            : "Enter your order ID to check the status of your purchase"}
        </p>
      </div>

      {/* Order Search Form */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
        <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder={isServiceOrder ? "Enter your booking ID" : "Enter your order ID (e.g., order_123456)"}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Tracking...
              </>
            ) : (
              "Track Order"
            )}
          </button>
        </form>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* Order Status */}
      {orderData && (
        <div className="space-y-6">
          {/* Order Summary Card */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold ">
                  {isServiceOrder ? 'Service Booking' : 'Order'} #{orderData.order_id}
                </h2>
                <p className="text-gray-600">Placed on {formatDate(orderData.created_at)}</p>
                {isServiceOrder && orderData.service_details?.project_title && (
                  <p className="text-blue-600 font-medium mt-1">
                    Project: {orderData.service_details.project_title}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(orderData.order_status)}`}>
                  {getStatusIcon(orderData.order_status)} {orderData.order_status}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(orderData.payment_status)}`}>
                  Payment: {orderData.payment_status}
                </span>
              </div>
            </div>

            {/* Order Progress Steps */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-700">
                  {isServiceOrder ? 'Service Progress' : 'Order Progress'}
                </h3>
                {!isServiceOrder && orderData.expected_delivery_latest && (
                  <span className="text-sm text-gray-500">
                    Estimated Delivery: {formatDate(orderData.expected_delivery_latest)}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between relative">
                {getStatusSteps(orderData).map((step, index, array) => (
                  <div key={step.id} className="flex flex-col items-center relative z-10 flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${
                      step.completed ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {step.completed ? '✓' : step.id}
                    </div>
                    <span className="text-xs text-center text-gray-600 whitespace-nowrap px-1">{step.name}</span>

                    {/* Connector line */}
                    {index < array.length - 1 && (
                      <div className={`absolute top-4 left-1/2 w-full h-0.5 ${
                        array[index + 1].completed ? 'bg-green-500' : 'bg-gray-200'
                      }`} style={{ transform: 'translateX(50%)' }}></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Order Items */}
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-700 mb-4">
                {isServiceOrder ? 'Service Details' : 'Order Items'}
              </h3>
              <div className="space-y-4">
                {orderData.items && orderData.items.length > 0 ? (
                  orderData.items.map((item, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-lg">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                        {item.reference_image_url ? (
                          <img
                            src={item.reference_image_url}
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-blue-100 flex items-center justify-center">
                            <span className="text-sm font-bold text-blue-800">
                              {isServiceOrder ? '🔧' : (item.product_name?.charAt(0) || '?')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium ">{item.product_name}</h4>
                        {isServiceOrder && item.project_title && (
                          <p className="text-sm text-gray-700 mt-1">
                            <strong>Project:</strong> {item.project_title}
                          </p>
                        )}
                        {isServiceOrder && item.project_description && (
                          <p className="text-sm text-gray-600 mt-1">
                            <strong>Description:</strong> {item.project_description}
                          </p>
                        )}
                        {!isServiceOrder && item.variation && (
                          <p className="text-sm text-gray-600">Variant: {item.variation}</p>
                        )}
                        <p className="text-sm text-gray-500">
                          {isServiceOrder ? 'Service' : 'Quantity'}: {item.order_quantity || 1}
                        </p>
                        
                        {/* Upfront Payment Info */}
                        {isServiceOrder && orderData.upfront_payment_percentage > 0 && (
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                            <p className="text-sm text-yellow-800">
                              <strong>Upfront Payment Required:</strong> {orderData.upfront_payment_percentage}% 
                              ({formatCurrency((item.per_unit_cost * orderData.upfront_payment_percentage) / 100)})
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(item.per_unit_cost * (item.order_quantity || 1))}</p>
                        {!isServiceOrder && (
                          <p className="text-sm text-gray-500">{formatCurrency(item.per_unit_cost)} each</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">No items found</p>
                )}
              </div>

              <div className="border-t mt-4 pt-4 flex justify-between items-center">
                <span className="font-semibold">Total Amount:</span>
                <span className="text-xl font-bold text-blue-600">{formatCurrency(orderData.total_amount)}</span>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-gray-700 mb-4">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Contact Details</h4>
                <p className="">{orderData.customer_name}</p>
                <p className="">{orderData.customer_email}</p>
                <p className="">{orderData.customer_phone}</p>
                
                {/* Only show address for product orders */}
                {!isServiceOrder && orderData.customer_address && (
                  <>
                    <p className=" mt-2">{orderData.customer_address}</p>
                  </>
                )}
              </div>

              <div>
                {!isServiceOrder ? (
                  <>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Delivery Information</h4>
                    <p className="">
                      <span className="font-medium">Expected Delivery:</span>{' '}
                      {formatDate(orderData.expected_delivery_earliest)} - {formatDate(orderData.expected_delivery_latest)}
                    </p>
                    {orderData.additional_info && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-500">Additional Notes:</p>
                        <p className="">{orderData.additional_info}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Service Information</h4>
                    <p className="">
                      <span className="font-medium">Service Type:</span> Custom Service
                    </p>
                    {orderData.service_details?.budget && (
                      <p className="">
                        <span className="font-medium">Agreed Budget:</span> {formatCurrency(orderData.service_details.budget)}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {/* <div className="border-t mt-6 pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={handleGenerateInvoice}
                className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              >
                Generate Invoice
              </button>
              
              <button
                onClick={handleSchedulePayment}
                className="px-4 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              >
                Schedule Payment Plan
              </button>
            </div> */}

            {/* Delivery Confirmation - Only for product orders */}
            {!isServiceOrder && (
              <div className="border-t mt-6 pt-6">
                <button
                  onClick={() => navigate(`/confirm-delivery/${orderData.order_id}`)}
                  className="px-4 py-2 w-full bg-blue-800 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                >
                  Confirm Delivery
                </button>
              </div>
            )}
          </div>

          {/* Support Information */}
          <div className="bg-blue-50 rounded-xl p-6">
            <h3 className="font-semibold text-blue-800 mb-2">Need Help?</h3>
            <p className="text-blue-700 mb-4">
              If you have any questions about your {isServiceOrder ? 'service booking' : 'order'}, please contact our support team.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={`mailto:support@paymeo.co?subject=Question about ${isServiceOrder ? 'Service Booking' : 'Order'} ${orderData.order_id}`}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Email Support
              </a>
              <a
                href="/contact"
                className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                Send a Message
              </a>

                   <a
                href="/request-refund"
                className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                Rquest a Refund
              </a>
            </div>
          </div>
        </div>
      ) }
         

      {/* Empty State */}
      {!orderData && !isLoading && !error && urlOrderId && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">{isServiceOrder ? '🔧' : '📦'}</span>
          </div>
          <h3 className="text-lg font-semibold  mb-2">
            {isServiceOrder ? 'Service Booking' : 'Order'} Not Found
          </h3>
          <p className="text-gray-600">
            We couldn't find {isServiceOrder ? 'a service booking' : 'an order'} with ID: <strong>{urlOrderId}</strong>
          </p>
          <p className="text-gray-600 mt-2">
            Please check your ID and try again, or contact support if you need assistance.
          </p>
        </div>
      )}
    </div>
  );
};

export default OrderTrackingPage;